<template>
  <FormStepInputs
    :step="{
      stepName: name,
      formInputs: content,
    }"
  >
    <template #accountAddressesTitle>
      <PanelTitle v-bind="getFormInput(props.content, 'accountAddressesTitle')"></PanelTitle>
    </template>
    <template #accountAddressesReview>
      <div v-if="accountAddressesSummaries && accountAddressesSummaries?.items?.length > 0" class="mt-5">
        <ReviewSummary
          :selfService="true"
          :reviewSummaries="[accountAddressesSummaries]"
          :isSelfService="true"
          :subheading="accountAddressesReview?.subheading"
          :summaryItemFooterOnLastSummaryOnly="true"
          :isConfirmationStep="false"
        >
        </ReviewSummary>
      </div>
    </template>
    <template #insuredAddressesTitle>
      <div
        v-if="
          hasCarInsuranceAddressLinks ||
          hasHomeInsuranceAddressLinks ||
          hasPetInsuranceAddressLinks ||
          hasOtherInsuranceAddressLinks
        "
        class="mt-8"
      >
        <PanelTitle v-bind="getFormInput(props.content, 'insuredAddressesTitle')"></PanelTitle>
      </div>
    </template>
    <template #carInsurancesReview>
      <div v-if="carReviewSummaries && carReviewSummaries?.items?.length > 0" class="mt-5">
        <ReviewSummary
          :selfService="true"
          :reviewSummaries="[carReviewSummaries]"
          :isSelfService="true"
          :subheading="carInsurancesReview?.subheading"
          :summaryItemFooterOnLastSummaryOnly="true"
          :isConfirmationStep="false"
        >
        </ReviewSummary>
      </div>
    </template>
    <template #divider1>
      <div
        v-if="hasCarInsuranceAddressLinks && (hasPetInsuranceAddressLinks || hasOtherInsuranceAddressLinks)"
        class="my-8 md:w-176"
      >
        <Divider></Divider>
      </div>
    </template>
    <template #notEditableAddressMessage>
      <div
        v-if="hasHomeInsuranceAddressLinks || hasPetInsuranceAddressLinks || hasOtherInsuranceAddressLinks"
        class="flex md:w-176 items-start mt-5"
      >
        <InformationBox
          :title="notEditableAddressMessage.title.value"
          :description="notEditableAddressMessage.content.value"
          :type="notEditableAddressMessage.messageType.value"
          :isCircle="true"
          :useButtonSlot="false"
        ></InformationBox>
      </div>
    </template>
    <template #homeInsurancesReview>
      <div v-if="homeReviewSummaries && homeReviewSummaries?.items?.length > 0" class="mt-5">
        <ReviewSummary
          :selfService="true"
          :reviewSummaries="[homeReviewSummaries]"
          :isSelfService="true"
          :subheading="homeInsurancesReview?.subheading"
          :summaryItemFooterOnLastSummaryOnly="true"
          :isConfirmationStep="false"
        >
        </ReviewSummary>
      </div>
    </template>
    <template #petInsurancesReview>
      <div v-if="petReviewSummaries && petReviewSummaries?.items?.length > 0" class="mt-5">
        <ReviewSummary
          :selfService="true"
          :reviewSummaries="[petReviewSummaries]"
          :isSelfService="true"
          :subheading="otherInsurancesReview?.subheading"
          :summaryItemFooterOnLastSummaryOnly="true"
          :isConfirmationStep="false"
        >
        </ReviewSummary>
      </div>
    </template>
    <template #otherInsurancesReview>
      <div v-if="otherReviewSummaries && otherReviewSummaries?.items?.length > 0" class="mt-5">
        <ReviewSummary
          :selfService="true"
          :reviewSummaries="[otherReviewSummaries]"
          :isSelfService="true"
          :subheading="otherInsurancesReview?.subheading"
          :summaryItemFooterOnLastSummaryOnly="true"
          :isConfirmationStep="false"
        >
        </ReviewSummary>
      </div>
    </template>
  </FormStepInputs>
  <div class="flex w-[640px] mt-14 items-start content-start gap-4 flex-wrap">
    <Link :field="discardChangesLink?.link" class="btn btn-tertiary btn-md">
      <div class="flex items-center space-x-2">
        <img src="@/assets/icons/arrow-left.svg" alt="left arrow" class="w-5 h-5 inline-block" />
        <span>{{ discardChangesLink?.link?.value?.text }}</span>
      </div>
    </Link>
  </div>
  <div class="!mt-10 mb-10 md:!mt-12 md:!mb-12">
    <Divider></Divider>
  </div>
  <FullScreenSpinner v-if="isLoadingOrSaving" :applyOpacity="true"></FullScreenSpinner>
</template>
<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import {
  Address,
  IAddress,
  InsuranceQuoteType,
  IReviewSummaryItem,
  ISitecoreFormGenericInput,
  PetCoverage,
  QuoteTypeCodes,
  ResidentialCoverage,
  VehicleCoverage,
} from '@/interfaces';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { useAccountStore, useAddressLookupStore } from '@/store';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import Divider from '@/components/Divider/Divider.vue';
import { AUSTRALIA_ISO, AUSTRALIA } from '@/constants/general-words.const';
import { buildIdentityAddress, buildInternationalAddressLine } from '@/utils/common-helper';
import { INameValue } from '@/interfaces/entities/name-value.interface';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import ReviewSummary from '@/components/ReviewSummary/ReviewSummary.vue';
import { IReviewSummary } from '@/interfaces';
import { SelfServiceService } from '@/services/store';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import { useI18n } from 'vue-i18n';
import { Link } from '@sitecore-jss/sitecore-jss-vue';

const accountStore = useAccountStore();

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();

const accountAddressesReview = ref(getFormInput(props.content, 'accountAddressesReview'));
const carInsurancesReview = ref(getFormInput(props.content, 'carInsurancesReview'));
const homeInsurancesReview = ref(getFormInput(props.content, 'homeInsurancesReview'));
const petInsurancesReview = ref(getFormInput(props.content, 'petInsurancesReview'));
const otherInsurancesReview = ref(getFormInput(props.content, 'otherInsurancesReview'));

const notEditableAddressMessage = ref(getFormInput(props.content, 'notEditableAddressMessage'));
const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));

const hasCarInsuranceAddressLinks = computed(() => {
  return (carReviewSummaries.value?.items?.length ?? 0) > 0;
});

const hasHomeInsuranceAddressLinks = computed(() => {
  return (homeReviewSummaries.value?.items?.length ?? 0) > 0;
});

const hasPetInsuranceAddressLinks = computed(() => {
  return (petReviewSummaries.value?.items?.length ?? 0) > 0;
});

const hasOtherInsuranceAddressLinks = computed(() => {
  return (otherReviewSummaries.value?.items?.length ?? 0) > 0;
});

const mapPinIcon =
  '<span class="bg-pin-icon icon mt-1 w-4 h-4 bg-no-repeat bg-contain inline-block mr-1" style="position:relative;bottom:-3px"></span>';

const mapPinIconDisabled =
  '<span class="bg-pin-icon icon w-4 h-4 bg-no-repeat bg-contain inline-block mr-1 disabled" style="opacity:0.3!important;position:relative;bottom:-3px"></span>';

const motorTypes = [
  QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive],
  QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability],
  QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability],
];

const homeTypes = [
  QuoteTypeCodes[InsuranceQuoteType.Home],
  QuoteTypeCodes[InsuranceQuoteType.HomeAndContents],
  QuoteTypeCodes[InsuranceQuoteType.Contents],
];

const petTypes = [QuoteTypeCodes[InsuranceQuoteType.PetComp]];

const accountAddressesSummaries = ref<IReviewSummary>();
const carReviewSummaries = ref<IReviewSummary>();
const homeReviewSummaries = ref<IReviewSummary>();
const petReviewSummaries = ref<IReviewSummary>();
const otherReviewSummaries = ref<IReviewSummary>();
const isLoadingOrSaving = ref<boolean>(false);

const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

onMounted(async () => {
  isLoadingOrSaving.value = true;
  accountAddressesSummaries.value = await getAccountAddressesReviewSummary();
  carReviewSummaries.value = await getCarReviewSummary();
  homeReviewSummaries.value = await getHomeReviewSummary();
  petReviewSummaries.value = await getPetReviewSummary();
  otherReviewSummaries.value = await getOtherReviewSummary();
  isLoadingOrSaving.value = false;
  pushAnalytics();
});

const getAccountAddressesReviewSummary = async () => {
  let summary: IReviewSummary;
  const addressesChangeLink = getFormInput(props.content, 'addressesChangeLink');

  summary = {
    formContentId: 'addressesSummary',
    summaryTitle: { value: '' },
    summarySubTitle: { value: '' },
    icon: { value: '' },
    items: [] as IReviewSummaryItem[],
  };

  const noAddressListed = 'No address listed';
  summary.items = [];
  const residentialAddress = accountStore.identityDetails?.addresses?.find((x) => x.addressType === 'Residential');

  let hasAddressListed = (residentialAddress?.formattedLong?.length ?? 0) > 0;
  let useResidentialContent =
    (residentialAddress?.formattedLong?.length ?? 0 > 0)
      ? buildIdentityAddress(residentialAddress as Address)
      : noAddressListed;

  if (
    residentialAddress &&
    residentialAddress?.country &&
    residentialAddress?.country !== AUSTRALIA_ISO &&
    residentialAddress?.country !== AUSTRALIA
  ) {
    const countries = (await lookupCountry()) ?? [];
    const country = countries.find((x: any) => x.value === residentialAddress?.country);

    if (country) {
      useResidentialContent = buildInternationalAddressLine(residentialAddress, country?.label ?? '', false);
    }
  }

  summary.items.push({
    title: {
      value: 'Residential address',
    },
    content: { value: wrapAccountAddressContentInDiv(useResidentialContent ?? '', hasAddressListed) },
    editUrl: { value: '' },
    showItem: true,
    editGoToStep: false,
    showEdit: false,
    showPrimaryLink: true,
    fields: {
      primaryLink: {
        value: {
          text: addressesChangeLink.link.value.text,
          href: addressesChangeLink.link.value.href,
        },
      },
    },
  } as unknown as IReviewSummaryItem);

  const postalAddress = accountStore.identityDetails?.addresses?.find((x) => x.addressType === 'Postal');
  hasAddressListed = (postalAddress?.formattedLong?.length ?? 0) > 0;
  let usePostalContent =
    (postalAddress?.formattedLong?.length ?? 0 > 0) ? buildIdentityAddress(postalAddress as Address) : noAddressListed;

  if (
    postalAddress &&
    postalAddress?.country &&
    postalAddress?.country !== AUSTRALIA_ISO &&
    postalAddress?.country !== AUSTRALIA
  ) {
    const countries = (await lookupCountry()) ?? [];
    const country = countries.find((x: any) => x.value === postalAddress?.country);

    if (country) {
      usePostalContent = buildInternationalAddressLine(postalAddress, country?.label ?? '', false);
    }
  }

  summary.items.push({
    title: {
      value: 'Postal address',
    },
    content: { value: wrapAccountAddressContentInDiv(usePostalContent ?? '', hasAddressListed) },
    editUrl: { value: '' },
    showItem: true,
    editGoToStep: false,
    showEdit: false,
    showPrimaryLink: true,
    fields: {
      primaryLink: {
        value: {
          text: addressesChangeLink.link.value.text,
          href: addressesChangeLink.link.value.href,
        },
      },
    },
  } as unknown as IReviewSummaryItem);

  return summary;
};

const getCarReviewSummary = async () => {
  let summary: IReviewSummary;

  summary = {
    formContentId: 'carSummary',
    summaryTitle: carInsurancesReview.value.title,
    summarySubTitle: carInsurancesReview.value.belowHeading,
    icon: { value: 'bg-car-filled-icon' },
    items: [] as IReviewSummaryItem[],
  };

  summary.items = [];

  const carChangeLocationLink = getFormInput(props.content, 'carChangeLocationLink');

  const policies = accountStore.identityDetails?.portfolio?.insurancePolicies?.filter((p) =>
    p.coverages.some((c) => motorTypes.indexOf(c.coverageCode) > -1)
  );

  if ((policies?.length ?? 0) > 0) {
    for (const key in policies) {
      if (Object.prototype.hasOwnProperty.call(policies, key)) {
        const policy = policies[key];

        for (const cKey in policy.coverages) {
          const coverage = policy.coverages[cKey];

          if (motorTypes.indexOf(coverage.coverageCode) == -1) {
            continue;
          }

          if (coverage.discriminator === 'VehicleCoverage') {
            try {
              const vehicleCoverage = coverage as VehicleCoverage;
              let quoteSessionResponse;
              try {
                quoteSessionResponse = await SelfServiceService.createQuoteSession(
                  policy.policyNumber,
                  SelfServiceSessionPages.ChangeVehicleLocationPage,
                  vehicleCoverage.id
                );
              } catch (error) {
                console.error(error);
              }

              summary.items.push({
                title: {
                  value: `${vehicleCoverage?.vehicle?.formattedSummary} (${vehicleCoverage?.vehicle?.registrationNumber})`,
                },
                content: { value: `${mapPinIcon} ${getFormattedVehicleLocation(vehicleCoverage)}` },
                editUrl: { value: '' },
                showItem: true,
                editGoToStep: false,
                showEdit: false,
                showPrimaryLink: true,
                fields: {
                  primaryLink: {
                    value: {
                      text: carChangeLocationLink.link.value.text,
                      href: `${carChangeLocationLink.link.value.href}${carChangeLocationLink.link.value.querystring.replace('{0}', quoteSessionResponse?.data)}`,
                    },
                  },
                },
              } as unknown as IReviewSummaryItem);
            } catch (error) {
              console.error(error);
            }
          }
        }
      }
    }
  }
  return summary;
};

const getFormattedVehicleLocation = (vehicleCoverage: VehicleCoverage) => {
  if (!vehicleCoverage) return '';
  const unableToDisplay = 'Address unable to display';
  try {
    if (vehicleCoverage?.garagingLocation) {
      let locationSegments = `${vehicleCoverage?.garagingLocation},${vehicleCoverage?.garageState}`?.split(',');
      const displayLocation =
        (locationSegments?.length ?? 0) > 0
          ? [locationSegments[0], locationSegments[2], locationSegments[1]].filter((o) => o).join(', ')
          : unableToDisplay;
      return displayLocation;
    }

    return unableToDisplay;
  } catch (error) {
    console.error(error);
  }

  //Fallback to just garage location just in case.
  return vehicleCoverage?.garagingLocation ?? unableToDisplay;
};

const getHomeReviewSummary = async () => {
  let summary: IReviewSummary;

  summary = {
    formContentId: 'homeSummary',
    summaryTitle: homeInsurancesReview.value.title,
    summarySubTitle: homeInsurancesReview.value.belowHeading,
    icon: { value: 'bg-home-icon' },
    items: [] as IReviewSummaryItem[],
  };

  summary.items = [];

  const allCoverages = accountStore.identityDetails?.portfolio?.insurancePolicies
    ?.flatMap((p) => p.coverages)
    .filter((c) => homeTypes.indexOf(c.coverageCode) > -1);

  if ((allCoverages?.length ?? 0) > 0) {
    for (const key in allCoverages) {
      if (Object.prototype.hasOwnProperty.call(allCoverages, key)) {
        const coverage = allCoverages[key];

        if (coverage.discriminator === 'ResidentialCoverage') {
          if (homeTypes.indexOf(coverage.coverageCode) == -1) {
            continue;
          }

          const residentialCoverage = coverage as ResidentialCoverage;

          if (residentialCoverage) {
            summary.items.push({
              title: {
                value: getShortDisplayAddress(residentialCoverage?.riskAddress),
              },
              content: { value: `${mapPinIcon} ${residentialCoverage?.riskAddress?.formattedLong}` },
              editUrl: { value: '' },
              showItem: true,
              editGoToStep: false,
              showEdit: false,
              showPrimaryLink: true,
            } as unknown as IReviewSummaryItem);
          }
        }
      }
    }
  }
  return summary;
};

const getPetReviewSummary = async () => {
  let summary: IReviewSummary;

  summary = {
    formContentId: 'petSummary',
    summaryTitle: petInsurancesReview.value.title,
    summarySubTitle: petInsurancesReview.value.belowHeading,
    icon: { value: 'bg-pet-filled-icon' },
    items: [] as IReviewSummaryItem[],
  };

  summary.items = [];

  const allCoverages = accountStore.identityDetails?.portfolio?.insurancePolicies
    ?.flatMap((p) => p.coverages)
    .filter((c) => petTypes.indexOf(c.coverageCode) > -1);

  if ((allCoverages?.length ?? 0) > 0) {
    for (const key in allCoverages) {
      if (Object.prototype.hasOwnProperty.call(allCoverages, key)) {
        const petCoverage = allCoverages[key] as PetCoverage;
        if (petCoverage) {
          if (petTypes.indexOf(petCoverage.coverageCode) == -1) {
            continue;
          }

          summary.items.push({
            title: {
              value: `${petCoverage?.petName?.toUpperCase()}, ${petCoverage?.petBreed?.toUpperCase()}`,
            },
            content: { value: `${mapPinIcon} ${petCoverage?.petLocationDescription}` },
            editUrl: { value: '' },
            showItem: true,
            editGoToStep: false,
            showEdit: false,
            showPrimaryLink: true,
          } as unknown as IReviewSummaryItem);
        }
      }
    }
  }
  return summary;
};

const getOtherReviewSummary = async () => {
  let summary: IReviewSummary;

  summary = {
    formContentId: 'otherSummary',
    summaryTitle: otherInsurancesReview.value.title,
    summarySubTitle: otherInsurancesReview.value.belowHeading,
    icon: { value: '' },
    items: [] as IReviewSummaryItem[],
  };

  summary.items = [];

  const coveragesAlreadyDisplayed = [...homeTypes, ...motorTypes, ...petTypes] as any[];
  const allCoverages = accountStore.identityDetails?.portfolio?.insurancePolicies
    ?.flatMap((p) => p.coverages)
    .filter((c) => coveragesAlreadyDisplayed.indexOf(c.coverageCode) == -1);

  if ((allCoverages?.length ?? 0) > 0) {
    for (const key in allCoverages) {
      if (Object.prototype.hasOwnProperty.call(allCoverages, key)) {
        const coverage = allCoverages[key];
        let location = '';

        let displayName = '';
        switch (coverage.discriminator) {
          case 'ResidentialCoverage':
            {
              const residentialCoverage = coverage as ResidentialCoverage;
              displayName =
                residentialCoverage && (displayName?.length ?? 0) === 0
                  ? residentialCoverage?.riskAddress?.formattedStreet
                  : '';
            }
            break;
          case 'VehicleCoverage':
            {
              const vehicleCoverage = coverage as VehicleCoverage;

              displayName = vehicleCoverage
                ? `${vehicleCoverage.vehicle?.formattedSummary?.toUpperCase()} (${vehicleCoverage.vehicle?.registrationNumber})`
                : '';

              location = getFormattedVehicleLocation(vehicleCoverage) ?? '';
            }
            break;
          case 'PetCoverage':
            {
              const petCoverage = coverage as PetCoverage;
              displayName = petCoverage && (displayName?.length ?? 0) === 0 ? petCoverage?.petName : '';
            }
            break;
        }

        if ((displayName?.length ?? 0) === 0) {
          displayName = 'Unknown';
        }

        const useLocationDetails =
          location !== 'Address unable to display'
            ? `${mapPinIcon} <span>${location}</span>`
            : `${mapPinIconDisabled} <span class='disabled opacity-60'>${location}</span>`;

        summary.items.push({
          title: {
            value: displayName,
          },
          content: { value: useLocationDetails },
          editUrl: { value: '' },
          showItem: true,
          editGoToStep: false,
          showEdit: false,
          showPrimaryLink: true,
        } as unknown as IReviewSummaryItem);
      }
    }

    return summary;
  }
};

const lookupCountry = async () => {
  try {
    const tempStore = useAddressLookupStore();
    const data: INameValue[] = await tempStore.getCountryLookup('', true, true);
    return (
      data?.map((item: INameValue) => ({
        label: item.name,
        value: item.value,
      })) || []
    );
  } catch {
    return [];
  }
};

const wrapAccountAddressContentInDiv = (content: string, hasAddressListed: boolean) => {
  const useMapPin = hasAddressListed ? mapPinIcon : mapPinIconDisabled;
  const divClass = `class="block text-base ${hasAddressListed ? ' text-racq-med-grey' : ' text-racq-light-grey'}"`;
  return `<div  ${divClass}>${useMapPin} <span>${content}</span></div>`;
};

const getShortDisplayAddress = (address: IAddress) => {
  let displayAddress = address.unit ? `${address.unit} ${address.streetNumber}` : `${address.streetNumber}`;

  displayAddress = `${displayAddress} ${address.street}`;

  return displayAddress.toUpperCase();
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};
</script>
